/**
 * Activity timeline (collaboration phase 3, the design's "Activity timeline and blame" board): everything that
 * happened in the project, in order, from vfs-service's `GET /collab/activity` (history plus the comment, handoff and
 * suggestion logs; presence is never stored). Swimlanes per participant over the range (solid: typing sessions,
 * hatched: agent runs, dots: everything else) and the feed, with filters by person, agent, type and service (one, a
 * group of several, or all: utils/activityServicesModel.js, remembered per project in this browser), and links into
 * Changes, Blame, the comment thread, the handoff note and the suggestion.
 *
 * Props: { projectId, isOpen, onClose, onOpenChanges?(link), onOpenSuggestion?(id), onOpenHandoff?(noteId), focus? }
 * (`focus.service`: the service a link asks for, `?tab=activity&service=api`). Blame and
 * comments open in the editor through window events (`openFile`, then `collab:blame` / `collab:openThread`).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowCounterClockwise, Copy, Database, FloppyDisk, Layout, Lock, Trash } from '@phosphor-icons/react';
import { ModalFrame } from '../Modal';
import { getActivity } from '../../services/CollabService';
import { restoreDeletedService } from '../../services/ServiceDeleteService';
import { notifyServicesChanged } from '../../hooks/useServiceFolders';
import {
  ACTIVITY_TYPES, DATA_EVENT_LABELS, axisTicks, blameLinkOf, eventLine, filterEvents, headlineOf, laneBlocks, linkOf, participantOf, participantsOf, rangeOf, eventsWithin, spanBetween,
} from '../../utils/collab3Model';
import { sinceFor } from '../../utils/changesModel';
import { openHistory } from '../../utils/workspaceModel';
import { usePlanTab } from '../../hooks/usePlanFeatures';
import { splitVfsPath } from '../../utils/presenceModel';
import { inkFor } from '../../utils/participantColors';
import { ago } from '../../utils/blameModel';
import { AuthorMark } from './ChangesPanel';
import './Collab.css';
import { CollabFilters } from './BottomSheet';
import HistoryEmpty from './HistoryEmpty';
import {
  PROJECT_FILES, filterByServices, pickFromLink, pickSummary, rememberPick, rememberedPick, serviceRows, togglePick,
} from '../../utils/activityServicesModel';

const RANGES = [
  { value: '1h', label: 'Last hour' },
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: 'all', label: 'All' },
];

const spanText = (span) => {
  const from = new Date(span.from);
  const to = new Date(span.to);
  const day = (d) => d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
  const time = (d) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return day(from) === day(to) ? `${day(from)}, ${time(from)}–${time(to)}` : `${day(from)} ${time(from)} – ${day(to)} ${time(to)}`;
};
const ONLY = [
  { value: 'all', label: 'Everyone' },
  { value: 'people', label: 'People' },
  { value: 'agents', label: 'AI agents' },
  { value: 'mine', label: 'Only mine' },
];

const agentSlot = (via) => (via === 'Flow' ? 3 : 4);
const slotOf = (who) => (who?.kind === 'agent' ? agentSlot(who.via) : who?.author?.slot ?? 0);

/** Opens a file in the editor, then asks it for blame or a comment thread. */
export function openInEditor(projectId, link) {
  if (!link?.path || typeof window === 'undefined') { return; }
  const { service, file } = splitVfsPath(link.path);
  window.dispatchEvent(new CustomEvent('openFile', { detail: { projectId, serviceName: service || undefined, filePath: file, lineNumber: link.line || undefined } }));
  if (link.to === 'file') { return; }
  const follow = link.to === 'blame'
    ? new CustomEvent('collab:blame', { detail: { projectId, path: link.path } })
    : new CustomEvent('collab:openThread', { detail: { projectId, path: link.path, threadId: link.threadId || null } });
  // After the editor has the file.
  setTimeout(() => window.dispatchEvent(follow), 800);
}

/**
 * The swimlanes over `range`. Dragging across a lane picks a span of time (`onSelect({ from, to })`): the timeline and
 * the feed then show only it.
 */
function Lanes({ events, range, onSelect = null }) {
  const lanes = useMemo(() => laneBlocks(events, range.from, range.to), [events, range]);
  const ticks = useMemo(() => axisTicks(range.from, range.to), [range]);
  const [drag, setDrag] = useState(null); // { a, b } fractions of the track while dragging
  const trackRef = useRef(null);
  if (lanes.length === 0) { return null; }
  const at = (event) => {
    const rect = (trackRef.current || event.currentTarget).getBoundingClientRect();
    return rect.width > 0 ? Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)) : 0;
  };
  const brush = onSelect ? {
    onPointerDown: (event) => {
      if (event.button !== 0) { return; }
      event.currentTarget.setPointerCapture?.(event.pointerId);
      const x = at(event);
      setDrag({ a: x, b: x });
    },
    onPointerMove: (event) => { if (drag) { const x = at(event); setDrag((d) => (d ? { ...d, b: x } : d)); } },
    onPointerUp: () => {
      // A click (under 1% of the track) is not a span.
      if (drag && Math.abs(drag.b - drag.a) >= 0.01) { onSelect(spanBetween(range, drag.a, drag.b)); }
      setDrag(null);
    },
    onPointerCancel: () => setDrag(null),
  } : {};
  const band = drag && Math.abs(drag.b - drag.a) > 0
    ? <span aria-hidden="true" className="collab-lane-brush" style={{ left: `${Math.min(drag.a, drag.b) * 100}%`, width: `${Math.abs(drag.b - drag.a) * 100}%` }} />
    : null;
  return (
    <div className={`collab-lanes${onSelect ? ' is-brushable' : ''}`} data-testid="activity-lanes" title={onSelect ? 'Drag across the timeline to see only that span of time' : undefined}>
      {lanes.map((lane) => {
        const ink = inkFor(slotOf(lane));
        return (
          <div className="collab-lane" key={lane.key}>
            <div className="collab-lane-who">
              <AuthorMark author={lane.author} size={20} via={lane.kind === 'agent' ? lane.via || 'Flow' : null} />
              <span className="collab-lane-name">{lane.kind === 'agent' ? `${lane.label}${lane.author ? ` · ${lane.author.name}` : ''}` : lane.label}</span>
            </div>
            <div className="collab-lane-track" {...brush}>
              {band}
              {lane.blocks.map((block, index) => (
                <span
                  className={`collab-lane-block is-${block.kind}`}
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  style={{ '--lane-ink': ink, left: `${block.left}%`, width: block.kind === 'dot' ? undefined : `${block.width}%` }}
                  title={eventLine(block.event)}
                />
              ))}
            </div>
          </div>
        );
      })}
      <div className="collab-lane">
        <div className="collab-lane-who" />
        <div className="collab-lane-axis" ref={trackRef}>
          {ticks.map((t) => <span key={t.left} style={{ left: `${t.left}%` }}>{t.label}</span>)}
        </div>
      </div>
    </div>
  );
}

Lanes.propTypes = { events: PropTypes.array.isRequired, onSelect: PropTypes.func, range: PropTypes.object.isRequired };

// The data and origin events (a Data tab change, a restore, a restore point saved, a template, a remix) and a repo
// flagged Read-only / No build or unflagged: an icon and a label before their summary.
const TYPE_ICONS = {
  'data-change': Database, 'data-restore': ArrowCounterClockwise, 'restore-point': FloppyDisk, template: Layout, remix: Copy, 'repo-flags': Lock,
  'service-delete': Trash, 'service-restore': ArrowCounterClockwise,
};

export function EventTypeTag({ type }) {
  const Icon = TYPE_ICONS[type];
  if (!Icon) { return null; }
  return (
    <span className="collab-type-tag" data-testid="activity-type-tag">
      <Icon aria-hidden size={13} />{DATA_EVENT_LABELS[type]}
    </span>
  );
}

EventTypeTag.propTypes = { type: PropTypes.string };

// Only an http(s) address opens from the feed.
const safeHref = (value) => (typeof value === 'string' && /^https?:\/\//i.test(value) ? value : null);

/**
 * A deleted service's Undo (vfs-service keeps the last ones, `deletedId`): puts it back with its files, pins, default
 * app, secrets and jobs. Says when it is done or why it could not (gone, or a service of that name exists again).
 */
function UndoDelete({ projectId, event, onDone }) {
  const [state, setState] = useState(null);
  const undo = async() => {
    setState({ busy: true });
    try {
      await restoreDeletedService(projectId, event.deletedId);
      notifyServicesChanged(projectId);
      setState({ done: true });
      onDone?.();
    } catch (e) {
      setState({ error: e?.message || 'It could not be restored.' });
    }
  };
  if (state?.done) { return <> · <span data-testid="activity-undo-done">Restored</span></>; }
  return (
    <>
      {' · '}
      <button className="collab-link" data-testid="activity-undo-delete" disabled={Boolean(state?.busy)} onClick={undo} type="button">{state?.busy ? 'Restoring…' : 'Undo'}</button>
      {state?.error && <span className="collab-error" data-testid="activity-undo-error" role="alert"> {state.error}</span>}
    </>
  );
}

UndoDelete.propTypes = { event: PropTypes.object.isRequired, onDone: PropTypes.func, projectId: PropTypes.string };

// A description longer than this may not fit its two clamped lines (on a phone): it gets "Read all".
const LONG_DESCRIPTION = 80;

function FeedItem({ event, projectId, onLink, onChanged = null }) {
  const [open, setOpen] = useState(false);
  const who = participantOf(event);
  const shows = usePlanTab();
  const found = linkOf(event);
  // A link into a tab the person's plan hides (Data's restore points, Configuration, Mail) is left out.
  const linkTab = found?.to === 'restore-points' ? 'data' : found?.to === 'view' ? found.view?.tab : null;
  const link = found && (!linkTab || shows(linkTab)) ? found : null;
  const blame = blameLinkOf(event);
  // What a session or agent run did (its commits' own words), with the counts below; else the summary as before.
  const headline = headlineOf(event);
  const canOpen = Boolean(headline && (headline.more.length > 0 || headline.description.length > LONG_DESCRIPTION));
  const files = event.files?.length ? ` · ${event.files.length} file${event.files.length === 1 ? '' : 's'}` : '';
  return (
    <div className="collab-feed-item" data-outcome={event.outcome || undefined} data-testid="activity-event" data-type={event.type}>
      {who ? <AuthorMark author={who.author} size={22} via={who.kind === 'agent' ? who.via || 'Flow' : null} /> : <span className="collab-feed-dot" />}
      <div style={{ flex: 1, minWidth: 0 }}>
        {headline ? (
          <div className={`collab-feed-headline${open ? '' : ' is-clamped'}`} data-testid="activity-headline" title={open ? undefined : headline.description}>
            <span className="collab-feed-who">{headline.who}</span>{' · '}<span data-testid="activity-description">{headline.description}</span>
          </div>
        ) : <div><EventTypeTag type={event.type} />{event.summary}</div>}
        {headline && open && headline.more.length > 0 && (
          <ul className="collab-feed-details" data-testid="activity-details">
            {headline.more.map((line) => <li key={line}>{line}</li>)}
          </ul>
        )}
        <div className="collab-muted collab-small">
          {ago(event.at)}
          {headline ? (headline.counts ? ` · ${headline.counts}` : '') : files}
          {canOpen && (
            <> · <button aria-expanded={open} className="collab-link" data-testid="activity-more" onClick={() => setOpen(!open)} type="button">
              {open ? 'Show less' : (headline.more.length > 0 ? `+${headline.more.length} more` : 'Read all')}
            </button></>
          )}
          {link && <> · <button className="collab-link" data-testid="activity-link" onClick={() => onLink(link)} type="button">{link.label}</button></>}
          {blame && <> · <button className="collab-link" onClick={() => openInEditor(projectId, blame)} type="button">{blame.label} {blame.path.split('/').pop()}</button></>}
          {/* A deploy Console Next finished: the app there, and the deploy in Console Next. */}
          {event.type === 'service-delete' && event.deletedId && <UndoDelete event={event} onDone={onChanged} projectId={projectId} />}
          {event.type === 'deploy' && safeHref(event.url) && <> · <a className="collab-link" data-testid="activity-deploy-app" href={safeHref(event.url)} rel="noopener noreferrer" target="_blank">Open app</a></>}
          {event.type === 'deploy' && safeHref(event.link) && <> · <a className="collab-link" data-testid="activity-deploy-console" href={safeHref(event.link)} rel="noopener noreferrer" target="_blank">Open in Console Next</a></>}
        </div>
      </div>
    </div>
  );
}

FeedItem.propTypes = { event: PropTypes.object.isRequired, onChanged: PropTypes.func, onLink: PropTypes.func.isRequired, projectId: PropTypes.string };

/** The Service filter: All services, then each service and Project files with their counts; several checked = a group. */
function ServiceFilter({ rows, picked, total, onPick }) {
  return (
    <>
      <div className="collab-filter-title" id="activity-service-title">Service</div>
      <div aria-labelledby="activity-service-title" className="collab-service-list" data-testid="activity-services" role="group">
        <label className="collab-radio collab-check">
          <input checked={picked.length === 0} data-testid="activity-service-all" onChange={() => onPick([])} type="checkbox" />
          <span className="collab-check-name">All services</span>
          <span className="collab-chip-count">{total}</span>
        </label>
        {rows.map((row) => (
          <label className="collab-radio collab-check" key={row.key} title={row.key === PROJECT_FILES ? 'brain/, the root files and changes to the whole project' : `services/${row.key}`}>
            <input checked={picked.includes(row.key)} data-testid="activity-service" onChange={() => onPick(togglePick(picked, row.key))} type="checkbox" value={row.key} />
            <span className="collab-check-name">{row.label}</span>
            <span className="collab-chip-count">{row.count}</span>
          </label>
        ))}
        {picked.length > 1 && <div className="collab-muted collab-small" data-testid="activity-service-group">A group of {picked.length}: what happened in any of them.</div>}
      </div>
    </>
  );
}

ServiceFilter.propTypes = { onPick: PropTypes.func.isRequired, picked: PropTypes.array.isRequired, rows: PropTypes.array.isRequired, total: PropTypes.number.isRequired };

// A link's `&service=` only says where to start: once the person picks, the address stops overriding their pick.
function dropServiceFromAddress() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab') !== 'activity' || !params.has('service')) { return; }
    params.delete('service');
    const query = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
  } catch { /* the address stays */ }
}

export default function ActivityPanel({ projectId, isOpen, onClose, onOpenChanges = null, onOpenSuggestion = null, onOpenHandoff = null, youId = null, embedded = false, focus = null }) {
  const [range, setRangeOnly] = useState('today');
  // A span picked on the timeline (`{ from, to }`), within the range; another range drops it.
  const [zoom, setZoom] = useState(null);
  const setRange = (next) => { setRangeOnly(next); setZoom(null); };
  const [only, setOnly] = useState('all');
  const [who, setWho] = useState([]);
  const [types, setTypes] = useState([]);
  const linked = focus?.service ? pickFromLink(focus.service) : null;
  const linkedKey = linked ? linked.join() : '';
  // The services picked ([] = all): a link's service first, else the pick remembered for this project.
  const [services, setServices] = useState(() => linked || rememberedPick(projectId));
  useEffect(() => {
    setServices(linkedKey ? linkedKey.split(',') : rememberedPick(projectId));
  }, [projectId, linkedKey]);
  const pickServices = (next) => {
    setServices(next);
    rememberPick(projectId, next);
    dropServiceFromAddress();
  };
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) { return; }
    try {
      setAnswer(await getActivity(projectId, { since: sinceFor(range), limit: 500 }));
      setError(null);
    } catch (e) {
      setError(e.message || 'The activity could not be loaded.');
    }
  }, [projectId, range]);

  useEffect(() => { if (isOpen) { load(); } }, [isOpen, load]);

  const all = useMemo(() => answer?.events || [], [answer]);
  // Everyone stays listed; their counts follow the services picked.
  const participants = useMemo(() => participantsOf(all, services.length ? filterByServices(all, services) : null), [all, services]);
  // The services' counts follow the other filters (who, what, show).
  const others = useMemo(() => filterEvents(all, { who, types, only, youId }), [all, who, types, only, youId]);
  const serviceList = useMemo(() => serviceRows(all, others, services), [all, others, services]);
  const filtered = useMemo(() => filterEvents(others, { services }), [others, services]);
  const shown = useMemo(() => (zoom ? eventsWithin(filtered, zoom.from, zoom.to) : filtered), [filtered, zoom]);
  const span = useMemo(() => zoom || rangeOf(filtered), [filtered, zoom]);

  const onLink = (link) => {
    if (link.to === 'comment' || link.to === 'blame' || link.to === 'file') { openInEditor(projectId, link); onClose(); return; }
    if (link.to === 'changes' && onOpenChanges) { onOpenChanges(link); if (!embedded) { onClose(); } return; }
    if (link.to === 'suggestion' && onOpenSuggestion) { onOpenSuggestion(link.suggestionId); onClose(); return; }
    if (link.to === 'handoff' && onOpenHandoff) { onOpenHandoff(link.noteId); }
    if (link.to === 'restore-points') { openHistory({ tab: 'data', focus: { view: 'restore', point: link.pointId || undefined } }); if (!embedded) { onClose(); } }
    // A secret changed, an email sent: the Configuration or Mail tab (utils/workspaceModel.js openHistory).
    if (link.to === 'view') { openHistory(link.view); }
  };
  const toggle = (list, set, value) => set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  const filtering = Boolean(who.length || types.length || services.length || only !== 'all' || zoom);
  const clearFilters = () => { setWho([]); setTypes([]); setOnly('all'); setZoom(null); pickServices([]); };

  if (!isOpen) { return null; }
  return (
    <ModalFrame className="collab-changes-modal" embedded={embedded} isOpen onClose={onClose} title="Activity" type="custom">
      <div className="collab-changes" data-testid="activity-panel">
        <CollabFilters summary={[zoom ? spanText(zoom) : RANGES.find((r) => r.value === range)?.label, pickSummary(services), who.length ? `${who.length} picked` : 'Everyone', types.length ? `${types.length} kinds` : 'everything'].join(' · ')} active={who.length + types.length + services.length + (only !== 'all' ? 1 : 0)}>
          <div className="collab-filter-title">When</div>
          <div className="collab-seg">
            {RANGES.map((r) => <button aria-pressed={range === r.value} className={range === r.value ? 'is-on' : ''} key={r.value} onClick={() => setRange(r.value)} type="button">{r.label}</button>)}
          </div>
          <div className="collab-filter-title">Show</div>
          {ONLY.map((o) => (
            <label className="collab-radio" key={o.value}>
              <input checked={only === o.value} disabled={o.value === 'mine' && !youId} name="collab-activity-only" onChange={() => setOnly(o.value)} type="radio" />
              {o.label}
            </label>
          ))}
          <ServiceFilter onPick={pickServices} picked={services} rows={serviceList} total={others.length} />
          <div className="collab-filter-title">Who</div>
          {participants.map((p) => (
            <button aria-pressed={who.includes(p.key)} className={`collab-chip${who.includes(p.key) ? ' is-on' : ''}`} data-testid="activity-who" key={p.key} onClick={() => toggle(who, setWho, p.key)} type="button">
              <AuthorMark author={p.author} size={20} via={p.kind === 'agent' ? p.via || 'Flow' : null} />
              <span className="collab-chip-name">{p.kind === 'agent' ? `${p.label}${p.author ? ` · ${p.author.name}` : ''}` : p.label}</span>
              <span className="collab-chip-count">{p.count}</span>
            </button>
          ))}
          <div className="collab-filter-title">What</div>
          <div className="collab-type-chips">
            {ACTIVITY_TYPES.map((t) => (
              <button aria-pressed={types.includes(t.value)} className={`collab-chip${types.includes(t.value) ? ' is-on' : ''}`} data-testid="activity-type" key={t.value} onClick={() => toggle(types, setTypes, t.value)} type="button">
                <span className="collab-chip-name" style={{ paddingLeft: 6 }}>{t.label}</span>
              </button>
            ))}
          </div>
        </CollabFilters>
        <div className="collab-groups">
          {error && <div className="collab-error">{error}</div>}
          {!answer && !error && <div className="collab-muted">Loading the activity…</div>}
          {zoom && (
            <div className="collab-zoom" data-testid="activity-zoom" role="status">
              <span>Showing {spanText(zoom)}</span>
              <button className="collab-link" data-testid="activity-zoom-reset" onClick={() => setZoom(null)} type="button">Show the whole period</button>
            </div>
          )}
          {answer && shown.length === 0 && (
            <HistoryEmpty onClear={filtering ? clearFilters : null} testId="activity-empty">
              {filtering ? 'Nothing happened in this period that matches the filters.' : 'Nothing happened in this period.'}
            </HistoryEmpty>
          )}
          {shown.length > 0 && <Lanes events={shown} onSelect={setZoom} range={span} />}
          {shown.length > 0 && (
            <div className="collab-feed" data-testid="activity-feed">
              {shown.map((event) => <FeedItem event={event} key={event.id} onChanged={load} onLink={onLink} projectId={projectId} />)}
            </div>
          )}
        </div>
      </div>
    </ModalFrame>
  );
}

ActivityPanel.propTypes = {
  embedded: PropTypes.bool,
  focus: PropTypes.shape({ service: PropTypes.string }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onOpenChanges: PropTypes.func,
  onOpenHandoff: PropTypes.func,
  onOpenSuggestion: PropTypes.func,
  projectId: PropTypes.string,
  youId: PropTypes.string,
};
